import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, 
  AlertTriangle, 
  IndianRupee, 
  Scale, 
  Upload,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  FileText,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const steps = [
  { id: 1, title: "Your Details", icon: User, description: "Tell us about yourself" },
  { id: 2, title: "The Scam", icon: AlertTriangle, description: "What happened?" },
  { id: 3, title: "Money Lost", icon: IndianRupee, description: "Financial details" },
  { id: 4, title: "Legal Status", icon: Scale, description: "Police complaint info" },
  { id: 5, title: "Evidence", icon: Upload, description: "Upload documents" },
];

const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Puducherry", "Chandigarh"
];

const contactMethods = [
  { value: "WhatsApp", label: "WhatsApp" },
  { value: "Phone Call", label: "Phone Call" },
  { value: "SMS", label: "SMS" },
  { value: "Email", label: "Email" },
  { value: "In-person", label: "In-person" },
];

const scamTypes = [
  { value: "Electricity Bill", label: "Electricity Bill Scam" },
  { value: "KYC Update", label: "KYC Update Fraud" },
  { value: "Investment Fraud", label: "Investment Fraud" },
  { value: "Grandchild in Trouble", label: "Grandchild in Trouble" },
  { value: "Parcel/Customs", label: "Parcel/Customs Scam" },
  { value: "Other", label: "Other" },
];

const transactionMethods = [
  { value: "UPI", label: "UPI (PhonePe, GPay, Paytm)" },
  { value: "NEFT", label: "NEFT" },
  { value: "RTGS", label: "RTGS" },
  { value: "Credit Card", label: "Credit Card" },
  { value: "Debit Card", label: "Debit Card" },
  { value: "Cash", label: "Cash" },
  { value: "Other", label: "Other" },
];

const investigatingAgencies = [
  { value: "None", label: "Not Yet Assigned" },
  { value: "Local Police", label: "Local Police" },
  { value: "Cyber Cell", label: "Cyber Cell" },
  { value: "CID", label: "CID" },
  { value: "CBI", label: "CBI" },
];

const ReportWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedCaseId, setSubmittedCaseId] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Victim Details
    name: "",
    age: "",
    city: "",
    state: "",
    phone: "",
    
    // Step 2: Scam Details
    contactMethod: "",
    scamType: "",
    incidentDate: null,
    
    // Step 3: Financial Impact
    amountLost: "",
    bankName: "",
    transactionMethod: "",
    
    // Step 4: Legal Status
    policeComplaintFiled: "no",
    firNumber: "",
    cyberCrimeAckNo: "",
    policeStation: "",
    filingDate: null,
    investigatingAgency: "None",
    
    // Step 5: Evidence
    firCopy: null,
    screenshots: [],
  });

  const [uploadedFiles, setUploadedFiles] = useState({
    firCopy: null,
    screenshots: []
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.age || !formData.city || !formData.state || !formData.phone) {
          toast.error("Please fill in all required fields");
          return false;
        }
        if (parseInt(formData.age) < 18 || parseInt(formData.age) > 120) {
          toast.error("Please enter a valid age");
          return false;
        }
        if (!/^\d{10}$/.test(formData.phone)) {
          toast.error("Please enter a valid 10-digit phone number");
          return false;
        }
        return true;
      case 2:
        if (!formData.contactMethod || !formData.scamType || !formData.incidentDate) {
          toast.error("Please fill in all required fields");
          return false;
        }
        return true;
      case 3:
        if (!formData.amountLost || !formData.bankName || !formData.transactionMethod) {
          toast.error("Please fill in all required fields");
          return false;
        }
        if (parseFloat(formData.amountLost) <= 0) {
          toast.error("Please enter a valid amount");
          return false;
        }
        return true;
      case 4:
        if (formData.policeComplaintFiled === "yes") {
          if (!formData.policeStation) {
            toast.error("Please enter police station name");
            return false;
          }
        }
        return true;
      case 5:
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFileUpload = async (file, type) => {
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      if (type === "firCopy") {
        setUploadedFiles(prev => ({ ...prev, firCopy: response.data }));
        updateFormData("firCopy", response.data.filename);
      } else {
        setUploadedFiles(prev => ({
          ...prev,
          screenshots: [...prev.screenshots, response.data]
        }));
        updateFormData("screenshots", [...formData.screenshots, response.data.filename]);
      }
      
      toast.success("File uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      if (file.type === "application/pdf" || file.type.startsWith("image/")) {
        handleFileUpload(file, type);
      } else {
        toast.error("Please upload PDF, JPG, or PNG files only");
      }
    });
  }, []);

  const handleFileSelect = (e, type) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      handleFileUpload(file, type);
    });
  };

  const removeFile = (type, index = null) => {
    if (type === "firCopy") {
      setUploadedFiles(prev => ({ ...prev, firCopy: null }));
      updateFormData("firCopy", null);
    } else if (index !== null) {
      setUploadedFiles(prev => ({
        ...prev,
        screenshots: prev.screenshots.filter((_, i) => i !== index)
      }));
      updateFormData("screenshots", formData.screenshots.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    setIsSubmitting(true);
    
    try {
      const payload = {
        victim: {
          name: formData.name,
          age: parseInt(formData.age),
          city: formData.city,
          state: formData.state,
          phone: formData.phone,
        },
        scam: {
          contact_method: formData.contactMethod,
          scam_type: formData.scamType,
          incident_date: formData.incidentDate ? format(formData.incidentDate, "yyyy-MM-dd") : "",
        },
        financial: {
          amount_lost: parseFloat(formData.amountLost),
          bank_name: formData.bankName,
          transaction_method: formData.transactionMethod,
        },
        legal: {
          police_complaint_filed: formData.policeComplaintFiled === "yes",
          fir_number: formData.firNumber || null,
          cyber_crime_ack_no: formData.cyberCrimeAckNo || null,
          police_station: formData.policeStation || null,
          filing_date: formData.filingDate ? format(formData.filingDate, "yyyy-MM-dd") : null,
          investigating_agency: formData.investigatingAgency,
        },
        evidence: {
          fir_copy: formData.firCopy || null,
          screenshots: formData.screenshots || [],
        },
      };
      
      const response = await axios.post(`${API}/cases`, payload);
      
      if (response.data.success) {
        setSubmittedCaseId(response.data.case_id);
        toast.success("Report submitted successfully!");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success Screen
  if (submittedCaseId) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] py-12" data-testid="report-success">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="text-center shadow-lg border-t-4 border-t-[#27ae60]">
              <CardContent className="p-8 md:p-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-[#27ae60]" />
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
                  Report Submitted Successfully!
                </h1>
                
                <p className="text-lg text-[#546e7a] mb-8">
                  Your complaint has been recorded. Please save your Case ID for tracking.
                </p>
                
                <div className="bg-slate-100 rounded-xl p-6 mb-8">
                  <p className="text-sm text-[#546e7a] uppercase tracking-wide mb-2">Your Case ID</p>
                  <p className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope']" data-testid="case-id-display">
                    {submittedCaseId}
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate(`/track?caseId=${submittedCaseId}`)}
                    className="bg-[#2c3e50] hover:bg-[#1a252f] text-white text-lg font-bold py-4 px-8 rounded-lg"
                    data-testid="track-case-btn"
                  >
                    Track Your Case
                  </Button>
                  <Button
                    onClick={() => navigate("/")}
                    variant="outline"
                    className="border-2 border-[#2c3e50] text-[#2c3e50] text-lg font-semibold py-4 px-8 rounded-lg"
                    data-testid="go-home-btn"
                  >
                    Go to Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 md:py-12" data-testid="report-wizard">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-2">
            Report a Scam
          </h1>
          <p className="text-lg text-[#546e7a]">
            Fill in the details step by step. We're here to help.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center ${
                  step.id < currentStep ? "text-[#27ae60]" :
                  step.id === currentStep ? "text-[#2c3e50]" : "text-[#95a5a6]"
                }`}
                data-testid={`step-indicator-${step.id}`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                    step.id < currentStep ? "bg-[#27ae60] text-white" :
                    step.id === currentStep ? "bg-[#2c3e50] text-white ring-4 ring-[#2c3e50]/20" :
                    "bg-slate-200 text-[#95a5a6]"
                  }`}
                >
                  {step.id < currentStep ? (
                    <CheckCircle className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </div>
                <span className="text-xs font-medium hidden md:block">{step.title}</span>
              </div>
            ))}
          </div>
          
          {/* Progress Bar */}
          <div className="wizard-progress">
            <div 
              className="wizard-progress-fill" 
              style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
            />
          </div>
          <p className="text-center text-[#546e7a] mt-2 text-lg">
            Step {currentStep} of 5: {steps[currentStep - 1].description}
          </p>
        </div>

        {/* Form Card */}
        <Card className="shadow-lg border-t-4 border-t-[#2c3e50]">
          <CardHeader className="pb-4">
            <CardTitle className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] flex items-center gap-3">
              {(() => {
                const StepIcon = steps[currentStep - 1].icon;
                return <StepIcon className="w-7 h-7" />;
              })()}
              {steps[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-6 md:p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Step 1: Victim Details */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Full Name <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={(e) => updateFormData("name", e.target.value)}
                        className="input-senior"
                        data-testid="input-name"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                          Age <span className="text-[#e74c3c]">*</span>
                        </Label>
                        <Input
                          type="number"
                          placeholder="Your age"
                          value={formData.age}
                          onChange={(e) => updateFormData("age", e.target.value)}
                          className="input-senior"
                          min="18"
                          max="120"
                          data-testid="input-age"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                          Phone Number <span className="text-[#e74c3c]">*</span>
                        </Label>
                        <Input
                          type="tel"
                          placeholder="10-digit mobile number"
                          value={formData.phone}
                          onChange={(e) => updateFormData("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                          className="input-senior"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                          City <span className="text-[#e74c3c]">*</span>
                        </Label>
                        <Input
                          type="text"
                          placeholder="Your city"
                          value={formData.city}
                          onChange={(e) => updateFormData("city", e.target.value)}
                          className="input-senior"
                          data-testid="input-city"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                          State <span className="text-[#e74c3c]">*</span>
                        </Label>
                        <Select value={formData.state} onValueChange={(value) => updateFormData("state", value)}>
                          <SelectTrigger className="input-senior" data-testid="select-state">
                            <SelectValue placeholder="Select your state" />
                          </SelectTrigger>
                          <SelectContent>
                            {indianStates.map(state => (
                              <SelectItem key={state} value={state} className="text-base py-3">
                                {state}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Scam Details */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        How were you contacted? <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Select value={formData.contactMethod} onValueChange={(value) => updateFormData("contactMethod", value)}>
                        <SelectTrigger className="input-senior" data-testid="select-contact-method">
                          <SelectValue placeholder="Select how scammer contacted you" />
                        </SelectTrigger>
                        <SelectContent>
                          {contactMethods.map(method => (
                            <SelectItem key={method.value} value={method.value} className="text-base py-3">
                              {method.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Type of Scam <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Select value={formData.scamType} onValueChange={(value) => updateFormData("scamType", value)}>
                        <SelectTrigger className="input-senior" data-testid="select-scam-type">
                          <SelectValue placeholder="Select the type of scam" />
                        </SelectTrigger>
                        <SelectContent>
                          {scamTypes.map(type => (
                            <SelectItem key={type.value} value={type.value} className="text-base py-3">
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Date of Incident <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="input-senior w-full justify-start text-left font-normal"
                            data-testid="input-incident-date"
                          >
                            {formData.incidentDate ? (
                              format(formData.incidentDate, "PPP")
                            ) : (
                              <span className="text-[#95a5a6]">Pick a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.incidentDate}
                            onSelect={(date) => updateFormData("incidentDate", date)}
                            disabled={(date) => date > new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}

                {/* Step 3: Financial Impact */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Total Amount Lost (in Rupees) <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#546e7a] text-lg">₹</span>
                        <Input
                          type="number"
                          placeholder="Enter amount"
                          value={formData.amountLost}
                          onChange={(e) => updateFormData("amountLost", e.target.value)}
                          className="input-senior pl-10"
                          min="0"
                          data-testid="input-amount-lost"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        Your Bank Name <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g., State Bank of India, HDFC Bank"
                        value={formData.bankName}
                        onChange={(e) => updateFormData("bankName", e.target.value)}
                        className="input-senior"
                        data-testid="input-bank-name"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                        How did you pay? <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <Select value={formData.transactionMethod} onValueChange={(value) => updateFormData("transactionMethod", value)}>
                        <SelectTrigger className="input-senior" data-testid="select-transaction-method">
                          <SelectValue placeholder="Select payment method" />
                        </SelectTrigger>
                        <SelectContent>
                          {transactionMethods.map(method => (
                            <SelectItem key={method.value} value={method.value} className="text-base py-3">
                              {method.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Step 4: Legal Status */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-4 block">
                        Have you filed a Police Complaint? <span className="text-[#e74c3c]">*</span>
                      </Label>
                      <RadioGroup
                        value={formData.policeComplaintFiled}
                        onValueChange={(value) => updateFormData("policeComplaintFiled", value)}
                        className="flex gap-6"
                        data-testid="radio-police-complaint"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="yes" id="yes" className="w-6 h-6" />
                          <Label htmlFor="yes" className="text-lg cursor-pointer">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="no" id="no" className="w-6 h-6" />
                          <Label htmlFor="no" className="text-lg cursor-pointer">No</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {formData.policeComplaintFiled === "yes" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-6 border-l-4 border-l-[#2c3e50] pl-6 bg-slate-50 p-6 rounded-r-lg"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                              FIR Number (if available)
                            </Label>
                            <Input
                              type="text"
                              placeholder="Enter FIR number"
                              value={formData.firNumber}
                              onChange={(e) => updateFormData("firNumber", e.target.value)}
                              className="input-senior"
                              data-testid="input-fir-number"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                              Cyber Crime Acknowledgement No.
                            </Label>
                            <Input
                              type="text"
                              placeholder="Online complaint number"
                              value={formData.cyberCrimeAckNo}
                              onChange={(e) => updateFormData("cyberCrimeAckNo", e.target.value)}
                              className="input-senior"
                              data-testid="input-cyber-ack"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                            Police Station Name <span className="text-[#e74c3c]">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Name of the police station"
                            value={formData.policeStation}
                            onChange={(e) => updateFormData("policeStation", e.target.value)}
                            className="input-senior"
                            data-testid="input-police-station"
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                              Date of Filing
                            </Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="input-senior w-full justify-start text-left font-normal"
                                  data-testid="input-filing-date"
                                >
                                  {formData.filingDate ? (
                                    format(formData.filingDate, "PPP")
                                  ) : (
                                    <span className="text-[#95a5a6]">Pick a date</span>
                                  )}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={formData.filingDate}
                                  onSelect={(date) => updateFormData("filingDate", date)}
                                  disabled={(date) => date > new Date()}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          
                          <div>
                            <Label className="text-base font-semibold text-[#2c3e50] mb-2 block">
                              Investigating Agency
                            </Label>
                            <Select value={formData.investigatingAgency} onValueChange={(value) => updateFormData("investigatingAgency", value)}>
                              <SelectTrigger className="input-senior" data-testid="select-agency">
                                <SelectValue placeholder="Select agency" />
                              </SelectTrigger>
                              <SelectContent>
                                {investigatingAgencies.map(agency => (
                                  <SelectItem key={agency.value} value={agency.value} className="text-base py-3">
                                    {agency.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Step 5: Evidence Upload */}
                {currentStep === 5 && (
                  <div className="space-y-8">
                    {/* FIR Copy Upload */}
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-4 block">
                        Upload FIR Copy (Optional)
                      </Label>
                      {!uploadedFiles.firCopy ? (
                        <div
                          className="upload-zone"
                          onDrop={(e) => handleDrop(e, "firCopy")}
                          onDragOver={(e) => e.preventDefault()}
                          data-testid="upload-fir-copy"
                        >
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileSelect(e, "firCopy")}
                            className="hidden"
                            id="fir-upload"
                          />
                          <label htmlFor="fir-upload" className="cursor-pointer block">
                            <FileText className="w-12 h-12 text-[#95a5a6] mx-auto mb-4" />
                            <p className="text-lg text-[#546e7a] font-medium">
                              Drag & Drop your FIR copy here
                            </p>
                            <p className="text-base text-[#95a5a6] mt-2">
                              or click to browse (PDF, JPG, PNG)
                            </p>
                          </label>
                        </div>
                      ) : (
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-6 h-6 text-[#27ae60]" />
                            <span className="text-base font-medium text-[#2c3e50]">
                              {uploadedFiles.firCopy.original_name}
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile("firCopy")}
                            className="text-[#e74c3c] hover:text-[#c0392b] p-2"
                            data-testid="remove-fir-copy"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Screenshots Upload */}
                    <div>
                      <Label className="text-base font-semibold text-[#2c3e50] mb-4 block">
                        Upload Screenshots (Optional)
                      </Label>
                      <div
                        className="upload-zone"
                        onDrop={(e) => handleDrop(e, "screenshots")}
                        onDragOver={(e) => e.preventDefault()}
                        data-testid="upload-screenshots"
                      >
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          onChange={(e) => handleFileSelect(e, "screenshots")}
                          className="hidden"
                          id="screenshots-upload"
                          multiple
                        />
                        <label htmlFor="screenshots-upload" className="cursor-pointer block">
                          <Upload className="w-12 h-12 text-[#95a5a6] mx-auto mb-4" />
                          <p className="text-lg text-[#546e7a] font-medium">
                            Drag & Drop screenshots here
                          </p>
                          <p className="text-base text-[#95a5a6] mt-2">
                            or click to browse (JPG, PNG)
                          </p>
                        </label>
                      </div>
                      
                      {uploadedFiles.screenshots.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {uploadedFiles.screenshots.map((file, index) => (
                            <div key={index} className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <CheckCircle className="w-6 h-6 text-[#27ae60]" />
                                <span className="text-base font-medium text-[#2c3e50]">
                                  {file.original_name}
                                </span>
                              </div>
                              <button
                                onClick={() => removeFile("screenshots", index)}
                                className="text-[#e74c3c] hover:text-[#c0392b] p-2"
                                data-testid={`remove-screenshot-${index}`}
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Disclaimer */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-base text-amber-800">
                        <strong>Note:</strong> All uploaded documents are kept confidential and encrypted. They will only be used for verification and investigation purposes.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button
                onClick={handlePrevious}
                disabled={currentStep === 1}
                variant="outline"
                className="text-lg font-semibold py-4 px-6 rounded-lg border-2 border-slate-300 disabled:opacity-50"
                data-testid="btn-previous"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              
              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  className="bg-[#2c3e50] hover:bg-[#1a252f] text-white text-lg font-bold py-4 px-8 rounded-lg"
                  data-testid="btn-next"
                >
                  Next
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-[#e74c3c] hover:bg-[#c0392b] text-white text-lg font-bold py-4 px-8 rounded-lg shadow-lg"
                  data-testid="btn-submit"
                >
                  {isSubmitting ? "Submitting..." : "Submit Report"}
                  <CheckCircle className="w-5 h-5 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportWizard;
